import { FeatureFlagContext, Features } from "@app/feature-flags";
import { CountyProjectContext } from "@app/projects";
import { ProjectRunConfigurationType, RegionSetStatusType } from "@app/projects/enumerators";
import {
    ClaimsActivityLineOfBusinessFilter,
    ClaimsActivityLineOfBusinessTypeEnum,
} from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import {
    PciDashboardFiltersContext,
    PciDashboardFiltersOverview,
    PciDashboardFiltersPayload,
    PciDashboardProcedure,
    PciDashboardScenarioFilters,
    PciDashboardSpecialty,
} from "@app/projects/network-assessment/pci-dashboard/shared";
import { NewPatientClaimsEnum } from "@app/projects/servicing-provider";
import { DropdownItem, MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { isEqual } from "lodash";
import React, { JSX, useContext, useEffect, useState } from "react";

interface PciDashboardFiltersProps {
  isLoading: boolean;
  isPca: boolean;
  onApplyFiltersClick: (filtersPayload: PciDashboardFiltersPayload) => void;
  showLineOfBusiness: boolean;
}

const PciDashboardFilters = (props: PciDashboardFiltersProps): JSX.Element => {
  const [hasFilterData, setHasFilterData] = useState<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);
  const projectContext = useContext(CountyProjectContext);
  const enabledFeatureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;

  const [searchText, setSearchText] = useState(""); // State in grandparent

  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);

  const newPatientClaimsToolTipText = "A New Patient Claim is defined as a new patient office visit which is represented by codes 99202 - 99205 on the previous 15 months of Patient Claims"


  useEffect(() => {
    if (props.showLineOfBusiness) {
      pciDashboardFiltersContext.setSelectedLineOfBusiness(ClaimsActivityLineOfBusinessTypeEnum.Total);
    }
  }, [props.showLineOfBusiness]);

  useEffect(() => {
    // the project run configuration filter is not currently part of the filters bar
    // changing it will trigger an update to all the widget tiles
    if (!!pciDashboardFiltersContext.projectRunConfiguration?.projectRunConfigurationId && (!props.isPca || (!!pciDashboardFiltersContext.selectedLineOfBusiness && !initialDataLoaded))) {
      onApplyFiltersClick();
      setInitialDataLoaded(true);
    }
  }, [pciDashboardFiltersContext.projectRunConfiguration, pciDashboardFiltersContext.selectedLineOfBusiness]);

  useEffect(() => {
    if (!!pciDashboardFiltersContext.projectRunConfiguration?.projectRunConfigurationId && initialDataLoaded) {
      onApplyFiltersClick();
    }
  }, [pciDashboardFiltersContext.projectRunConfiguration])

  useEffect(() => {
    if (pciDashboardFiltersContext.serviceAreas.length === 0 || pciDashboardFiltersContext.specialties.length === 0) {
      setHasFilterData(false);
    } else {
      setHasFilterData(true);
    }
  }, [pciDashboardFiltersContext.serviceAreas, pciDashboardFiltersContext.specialties]);

  const isLoading = (): boolean => {
    return props.isLoading || pciDashboardFiltersContext.filtersLoading;
  }

  const applyDisabled = (): boolean => {
    // do not allow button interactions if ANYTHING is loading
    if (isLoading()) {
      return true;
    }

    const countiesEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.countySSAs.sort((a, b) => a.localeCompare(b)),
      pciDashboardFiltersContext.selectedCounties.sort((a, b) => a.localeCompare(b))
    );

    const countyStatusesEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.countyStatuses?.sort((a, b) => a.localeCompare(b)),
      pciDashboardFiltersContext.selectedCountyStatuses.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const lineOfBusinessEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.lineOfBusiness,
      pciDashboardFiltersContext.selectedLineOfBusiness
    );

    const newPatientClaimsFiltersEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.newPatientClaims?.sort((a, b) => a.localeCompare(b)),
      pciDashboardFiltersContext.selectedNewPatientClaimsFilters.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const specialtiesEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.specialtyGroupCodes?.sort((a, b) => a.localeCompare(b)),
      pciDashboardFiltersContext.selectedSpecialties.map(x => x.key.specialtyGroupCode).sort((a, b) => a.localeCompare(b))
    );

    const proceduresEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.procedureCodes?.sort((a, b) => a.localeCompare(b)),
      pciDashboardFiltersContext.selectedProcedures.map(x => x.key.code).sort((a, b) => a.localeCompare(b))
    );

    const scenariosEqual = isEqual(pciDashboardFiltersContext.displayedFilters?.scenario, pciDashboardFiltersContext.selectedScenario) &&
      isEqual(pciDashboardFiltersContext.displayedFilters?.compareScenario, pciDashboardFiltersContext.selectedCompareScenario);

    // if the "compare timeslices"" checkbox is checked, both timeslice dropdowns must have values and must not be equal to each other
    const scenariosValid = !pciDashboardFiltersContext.compareTimeslices || (
      pciDashboardFiltersContext.compareTimeslices && !!pciDashboardFiltersContext.selectedScenario && !!pciDashboardFiltersContext.selectedCompareScenario
    );

    // in order to avoid unnecessary api calls, prevent the user from applying filters if
    // a.) the currently-selected filters are the same as those that were used for what is already displayed
    // b.) at least one county, one specialty, and a line of business (if shown) are not selected
    if ((!countiesEqual || !countyStatusesEqual || !specialtiesEqual || !lineOfBusinessEqual || !newPatientClaimsFiltersEqual || !scenariosEqual || !proceduresEqual) && (
      pciDashboardFiltersContext.selectedCounties.length > 0 &&
      /// only require at least one county status to be selected when the county status feature flag is turned on and there are county status options present
      ((!!pciDashboardFiltersContext.selectedCountyStatuses.length && !!pciDashboardFiltersContext.countyStatuses.length) || !pciDashboardFiltersContext.countyStatuses.length) &&
      (pciDashboardFiltersContext.selectedSpecialties.length > 0 || pciDashboardFiltersContext.selectedProcedures.length > 0) && (
        props.showLineOfBusiness && !!pciDashboardFiltersContext.selectedLineOfBusiness || !props.showLineOfBusiness
      )
    ) && scenariosValid &&
      (!props.isPca || !enabledFeatureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) ||
      !!pciDashboardFiltersContext.selectedNewPatientClaimsFilters.length ||
      (pciDashboardFiltersContext.selectedNewPatientClaimsFilters.length == 0 && pciDashboardFiltersContext.displayedFilters.newPatientClaims.length == 0))
    ) {
      return false;
    }

    return true;
  }

  const clearDisabled = (): boolean => {
    // restoring filters to their defaults is unnecessary if the currently-selected filters are already the defaults
    if (!isLoading() && (
      pciDashboardFiltersContext.selectedCounties.length !== pciDashboardFiltersContext.counties.length ||
      pciDashboardFiltersContext.selectedSpecialties.length !== pciDashboardFiltersContext.specialties.length ||
      pciDashboardFiltersContext.selectedProcedures.length !== pciDashboardFiltersContext.procedures.length ||
      (props.showLineOfBusiness && pciDashboardFiltersContext.selectedLineOfBusiness !== ClaimsActivityLineOfBusinessTypeEnum.Total) ||
      (props.isPca && pciDashboardFiltersContext.selectedNewPatientClaimsFilters.length !== pciDashboardFiltersContext.newPatientClaimsFilters.length) ||
      !!pciDashboardFiltersContext.selectedCountyStatuses.map(x => x.key).includes(RegionSetStatusType.Opportunity) ||
      pciDashboardFiltersContext.selectedCountyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length !== pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length ||
      pciDashboardFiltersContext.compareTimeslices || !!pciDashboardFiltersContext.selectedScenario || !!pciDashboardFiltersContext.selectedCompareScenario
    )) {
      return false;
    }

    return true;
  }

  const onApplyFiltersClick = (): void => {
    const filtersPayload: PciDashboardFiltersPayload = pciDashboardFiltersContext.getFiltersPayload();

    filtersPayload.procedureCodes = pciDashboardFiltersContext.selectedProcedures.length === pciDashboardFiltersContext.procedures.length ? [] : filtersPayload.procedureCodes;

    props.onApplyFiltersClick(filtersPayload);

    // onApplyFiltersClick is executed on initial load, however,
    // we will only want to set the displayed filters here if it's not the initial load,
    // since they will be set after the filters are retrieved from the api in this case
    if (isFirstLoad) {
      setIsFirstLoad(false);
    } else {
      pciDashboardFiltersContext.setDisplayedFilters(filtersPayload);
    }
  }

  const getNewPatientClaimsToolTip = (): JSX.Element => {
    return (<div className="pci-dashboard__tooltip-text-div" >{newPatientClaimsToolTipText}</div>);
  }

  const getDualMembershipAliases = (): DropdownItem<string>[] => {
    const dualMembershipAliases: DropdownItem<string>[] = [];

    if (!!projectContext?.runConfiguration && !!projectContext?.project?.projectRunConfigurations) {
      const companionRunConfiguration = projectContext.project.projectRunConfigurations.find((config) => config.projectRunConfigurationId === projectContext.runConfiguration.companionProjectRunConfigurationId);

      const runConfigurationAlias: DropdownItem<string> = {
        key: projectContext.runConfiguration.projectRunConfigurationType,
        name: projectContext.runConfiguration.memberSetAlias,
      };

      const companionRunConfigurationAlias: DropdownItem<string> = {
        key: companionRunConfiguration.projectRunConfigurationType,
        name: companionRunConfiguration.memberSetAlias,
      };

      if (projectContext.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
        dualMembershipAliases.push(runConfigurationAlias);
        dualMembershipAliases.push(companionRunConfigurationAlias);
      } else if (companionRunConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
        dualMembershipAliases.push(companionRunConfigurationAlias);
        dualMembershipAliases.push(runConfigurationAlias);
      }
    }

    return dualMembershipAliases;
  }

  const handleSearchTextChange = (searchText: string) => {
    setSearchText(searchText);
    const filtered = searchText === "" ? [] : pciDashboardFiltersContext.procedures.filter(option =>
      option.name.toLowerCase().includes(searchText.toLowerCase())
    );
    pciDashboardFiltersContext.setFilteredProcedures(filtered);
  };


  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card card-static">
            <div className="card-body">
              <div className="row">
                <div className="col-12">
                  <label className="label-m mb-2">
                    Filters
                  </label>
                </div>
              </div>
              <div className="row">
                <div className="col-md-10">
                  <div className="row mb-3">
                    <MultiselectNestedDropdown
                      customSelectInputText={pciDashboardFiltersContext.counties.length === pciDashboardFiltersContext.selectedCounties.length ? "All" : null}
                      disabled={pciDashboardFiltersContext.filtersLoading || !hasFilterData}
                      items={pciDashboardFiltersContext.serviceAreas}
                      label="Service Area"
                      testId="pci-dashboard__filters--service-area"
                      containerClassName="col-md-3"
                      selectedItems={pciDashboardFiltersContext.selectedCounties}
                      onSelectionChanged={(items) => pciDashboardFiltersContext.setSelectedCounties(items)}
                    />
                    {(!!pciDashboardFiltersContext.countyStatuses.length) && (
                      <div className="col-md-3">
                        <MultiselectDropdown
                          containerClassName="d-block"
                          customSelectInputText={pciDashboardFiltersContext.countyStatuses.length === pciDashboardFiltersContext.selectedCountyStatuses.length ? "All" : null}
                          disabled={pciDashboardFiltersContext.filtersLoading}
                          items={pciDashboardFiltersContext.countyStatuses}
                          onSelectionChanged={(selectedCountyStatuses: DropdownItem<RegionSetStatusType>[]) => { pciDashboardFiltersContext.setSelectedCountyStatuses(selectedCountyStatuses) }}
                          label="County Status"
                          selectedItems={pciDashboardFiltersContext.selectedCountyStatuses}
                          testId="pci-dashboard__filters--county-status"
                        />
                      </div>
                    )}
                    <div className="col-md-3">
                      <MultiselectDropdown
                        containerClassName="d-block"
                        customSelectInputText={pciDashboardFiltersContext.specialties.length === pciDashboardFiltersContext.selectedSpecialties.length ? "All" : null}
                        disabled={pciDashboardFiltersContext.filtersLoading || !hasFilterData}
                        items={pciDashboardFiltersContext.specialties}
                        onSelectionChanged={(selectedSpecialties: DropdownItem<PciDashboardSpecialty>[]) => { pciDashboardFiltersContext.setSelectedSpecialties(selectedSpecialties) }}
                        label="Specialty"
                        selectedItems={pciDashboardFiltersContext.selectedSpecialties}
                        testId="pci-dashboard__filters--specialty"
                      />
                    </div>
                    {props.showLineOfBusiness && (
                      <div className="col-md-2">
                        <ClaimsActivityLineOfBusinessFilter
                          disabled={pciDashboardFiltersContext.filtersLoading || !hasFilterData}
                          selectedLineOfBusiness={pciDashboardFiltersContext.selectedLineOfBusiness}
                          setSelectedLineOfBusiness={pciDashboardFiltersContext.setSelectedLineOfBusiness}
                          testId="pci-dashboard__filters--line-of-business"
                          project={projectContext.project}
                        />
                      </div>
                    )}
                  </div>

                  <div className="row">
                    {props.isPca &&
                      enabledFeatureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) &&
                      !!pciDashboardFiltersContext.newPatientClaimsFilters.length && (
                      <div className="col-md-2">
                        <MultiselectDropdown
                          containerClassName="d-block"
                          customSelectInputText={pciDashboardFiltersContext.newPatientClaimsFilters.length === pciDashboardFiltersContext.selectedNewPatientClaimsFilters.length ? "All" : null}
                          disabled={pciDashboardFiltersContext.filtersLoading || !hasFilterData}
                          items={pciDashboardFiltersContext.newPatientClaimsFilters}
                          onSelectionChanged={(selectedNewPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[]) => { pciDashboardFiltersContext.setSelectedNewPatientClaimsFilters(selectedNewPatientClaimsFilters) }}
                          label="New Patient Claims"
                          selectedItems={pciDashboardFiltersContext.selectedNewPatientClaimsFilters}
                          testId="pci-dashboard__filters--new-patient-claims-filters"
                          validationTooltipContent={getNewPatientClaimsToolTip()}
                          showTooltipOnOverflow={true}
                        />
                      </div>
                    )}
                    {((props.isPca && (enabledFeatureFlags.has(Features.PCI_DASHBOARD_PROCEDURE_SEARCH))) || (!props.isPca && enabledFeatureFlags.has(Features.PCI_DASHBOARD_CPT_SEARCH_EE) && enabledFeatureFlags.has(Features.PCI_DASHBOARD_PROCEDURE_SEARCH))) && pciDashboardFiltersContext.procedures.length > 0 && (
                      <div className="col-md-6">
                          <MultiselectDropdown
                          containerClassName="d-block"
                          customSelectInputText={null}
                          disabled={pciDashboardFiltersContext.filtersLoading || !hasFilterData}
                          items={!!searchText ? pciDashboardFiltersContext.filteredProcedures : pciDashboardFiltersContext.selectedProcedures.concat(pciDashboardFiltersContext.filteredProcedures)}
                          onSelectionChanged={(selectedProcedures: DropdownItem<PciDashboardProcedure>[]) => { pciDashboardFiltersContext.setSelectedProcedures(selectedProcedures) }}
                          onSearchTextChange={handleSearchTextChange}
                          label="Search Top Procedures"
                          selectedItems={pciDashboardFiltersContext.selectedProcedures}
                          testId="pci-dashboard__filters--procedure"
                          hideSelectAll={true}
                          showTooltipOnOverflow={true}
                          validationTooltipContent="This dropdown searches the top 5 procedure codes across all available lines of business, regardless of your current filters"
                          autofocusSearch={true}
                          />
                        </div>
                    )}
                  </div>

                  {pciDashboardFiltersContext.scenarioTypes.length > 0 && (
                    <PciDashboardScenarioFilters
                      hasFilterData={hasFilterData}
                    />
                  )}
                </div>
                <div className="col-md-2 d-flex align-items-end justify-content-end">
                  <div>
                    <QESButton
                      disabled={applyDisabled() || !hasFilterData}
                      qesTestId="pci-dashboard__filters--apply"
                      qesType="secondary"
                      onClick={() => onApplyFiltersClick()}
                      className="pb-0"
                    >
                      Apply
                    </QESButton>

                    <QESButton
                      disabled={clearDisabled() || !hasFilterData}
                      qesTestId="pci-dashboard__filters--clear"
                      qesType="secondary-danger"
                      onClick={() => pciDashboardFiltersContext.onClearFiltersClick(props.showLineOfBusiness)}
                      className="pb-0"
                    >
                      Clear
                    </QESButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!!pciDashboardFiltersContext?.projectRunConfiguration?.companionProjectRunConfigurationId && (
          <div className="row">
            <div className="col-3">
              <div className="d-flex align-items-center mb-3">
                <label className="label-s mr-3 mb-0 d-inline-block" htmlFor="pci-dashboard__filters--project-run-configuration-filter">Display</label>
                <QESSelect
                  containerClassName="d-inline-block"
                  disabled={props.isLoading}
                  testId="pci-dashboard__filters--project-run-configuration-filter"
                  hideLabel={true}
                  label="Project Run Configuration Filter"
                  onChange={(e) => projectContext.setDualMembershipMode(false, e.target.value as ProjectRunConfigurationType)}
                  value={pciDashboardFiltersContext.projectRunConfiguration?.projectRunConfigurationType || ""}
                >
                  {getDualMembershipAliases().map((dualMembership, index) => (
                    <option
                      key={index}
                      value={dualMembership.key}
                    >
                      {dualMembership.name}
                    </option>
                  ))}
                </QESSelect>
              </div>
            </div>
          </div>
      )}

      <PciDashboardFiltersOverview
        showLineOfBusiness={props.showLineOfBusiness}
        showNewPatientClaims={props.isPca && enabledFeatureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT)}
      />
    </React.Fragment>
  );
}

export default PciDashboardFilters;